import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Hammer, ArrowLeft, FileCode2, Folder, FolderOpen, Loader2, Cpu, Package,
  Trash2, Download, GitBranch, Sparkles, NotebookPen, Terminal, ShieldAlert,
  ChevronRight, ChevronDown, Boxes, PlayCircle, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { api, API } from "../lib/api";
import ReadAlongside from "../components/ReadAlongside";

function buildTree(files) {
  const root = { name: "", path: "", children: {}, files: [], isDir: true };
  files.forEach((f) => {
    const parts = f.path.split("/");
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (!node.children[key]) {
        node.children[key] = { name: key, path: parts.slice(0, i + 1).join("/"), children: {}, files: [], isDir: true };
      }
      node = node.children[key];
    }
    node.files.push(f);
  });
  return root;
}
function flattenVisible(node, expanded, depth, out) {
  const dirs = Object.values(node.children).sort((a, b) => a.name.localeCompare(b.name));
  const files = [...node.files].sort((a, b) => a.name.localeCompare(b.name));
  for (const d of dirs) {
    const isOpen = expanded[d.path] ?? depth < 1;
    out.push({ kind: "dir", path: d.path, name: d.name, depth, isOpen });
    if (isOpen) flattenVisible(d, expanded, depth + 1, out);
  }
  for (const f of files) {
    out.push({ kind: "file", id: f.id, name: f.name, path: f.path, depth, file: f });
  }
}

const STATUS_STEPS = [
  { id: "configured",         label: "Configured" },
  { id: "building",           label: "Building" },
  { id: "build_successful",   label: "Build OK" },
  { id: "packaged",           label: "Packaged" },
  { id: "verified",           label: "Verified" },
];

export default function KailashWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();

  const [project, setProject] = useState(null);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState(null);
  const [contentBusy, setContentBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const [targets, setTargets] = useState(null); // { distros, arches, package_formats }
  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [mode, setMode] = useState("file"); // 'file' | 'config'
  const [busy, setBusy] = useState(false);
  const [showCross, setShowCross] = useState(false);
  const [crossTargets, setCrossTargets] = useState([]);
  const [crossBusy, setCrossBusy] = useState(false);
  const [crossResult, setCrossResult] = useState(null);

  // target picker state
  const [pick, setPick] = useState({ distro: "ubuntu", version: "24.04 LTS", arch: "x86_64", package_format: "deb", notes: "" });

  useEffect(() => {
    api.get(`/kailash/projects/${id}`)
      .then(({ data }) => setProject(data))
      .catch(() => { toast.error("Could not load project"); nav("/kailash"); });
    api.get("/kailash/targets").then(({ data }) => setTargets(data));
    api.get(`/kailash/projects/${id}/build-configs`).then(({ data }) => setConfigs(data.configs || []));
  }, [id, nav]);

  const flatFiles = useMemo(() => {
    if (!project?.files?.length) return [];
    const tree = buildTree(project.files);
    const out = [];
    flattenVisible(tree, expanded, 0, out);
    return out;
  }, [project, expanded]);

  const distroMeta = useMemo(() => targets?.distros.find((d) => d.id === pick.distro), [targets, pick.distro]);
  const family = distroMeta?.family || "any";
  const allowedFormats = targets?.package_formats?.[family] || [];

  // Keep pick.package_format in sync with allowed formats
  useEffect(() => {
    if (!allowedFormats.length) return;
    const ids = allowedFormats.map((f) => f.id);
    if (!ids.includes(pick.package_format)) {
      setPick((p) => ({ ...p, package_format: ids[0] }));
    }
  }, [allowedFormats.length, pick.package_format]);

  const openFile = async (fileId) => {
    setContentBusy(true);
    setMode("file");
    setActiveConfig(null);
    try {
      const { data } = await api.get(`/kailash/projects/${id}/files/${fileId}`);
      setSelected(data);
      setContent(data.content);
    } catch { toast.error("Could not load file"); }
    finally { setContentBusy(false); }
  };

  const openConfig = (cfg) => {
    setActiveConfig(cfg);
    setMode("config");
  };

  const generateConfig = async () => {
    if (!project) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/kailash/projects/${id}/build-configs`, pick);
      setConfigs([data, ...configs]);
      openConfig(data);
      toast.success(`Generated config for ${data.distro_name} · ${data.arch}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const deleteConfig = async (cfgId) => {
    if (!window.confirm("Delete this build configuration?")) return;
    try {
      await api.delete(`/kailash/build-configs/${cfgId}`);
      setConfigs(configs.filter((c) => c.id !== cfgId));
      if (activeConfig?.id === cfgId) { setActiveConfig(null); setMode("file"); }
    } catch { toast.error("Could not delete"); }
  };

  const downloadConfig = (cfg) => {
    const bundle = {
      project: project.name,
      target: `${cfg.distro_name} · ${cfg.version || "any"} · ${cfg.arch} · ${cfg.package_format}`,
      generated_at: cfg.created_at,
      configuration: cfg.generated,
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kailash-${cfg.distro}-${cfg.arch}-${cfg.package_format}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleDir = (path) => setExpanded((e) => ({ ...e, [path]: !(e[path] ?? false) }));

  if (!project || !targets) {
    return <div className="container-narrow py-24 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-ink2" /></div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6" data-testid="kailash-workspace">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <button onClick={() => nav("/kailash")} className="text-xs text-ink2 hover:text-ink inline-flex items-center gap-1 mb-1"><ArrowLeft className="w-3 h-3" /> All projects</button>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-semibold">{project.name}</h1>
            <span className="text-xs text-ink2">{project.understanding?.file_count || 0} files · {project.understanding?.primary_language || "mixed"}</span>
            {project.source === "git" && (
              <span className="inline-flex items-center gap-1 text-xs text-ink2 font-mono"><GitBranch className="w-3 h-3" /> {project.git_full_name}@{project.git_branch}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/knuth`} className="btn-ghost text-xs" title="Open Knuth with the same project idea">
            <NotebookPen className="w-3.5 h-3.5" /> Document with Knuth
          </Link>
        </div>
      </div>

      {/* Read alongside — chapters from the curriculum that pair with a build task */}
      <div className="mb-4">
        <ReadAlongside
          seeds={`linux build package release ${project.understanding?.primary_language || ""} deb rpm compilation ${project.name || ""}`}
          eyebrow="Read alongside as you build"
          title="chapters from the curriculum for this build"
          askPrompt={`I'm packaging "${project.name}" in Kailash. What curriculum ideas will help me ship a clean Linux release?`}
          defaultOpen={false}
          testId="kailash-read-alongside"
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left panel — project + files */}
        <aside className="col-span-3 space-y-4">
          <div className="rounded-2xl border border-border bg-cream">
            <div className="px-3 py-2.5 border-b border-border text-xs uppercase tracking-widest font-bold text-ink2 flex items-center gap-2">
              <Folder className="w-3.5 h-3.5" /> Project
            </div>
            <div className="p-3 text-xs text-ink2 space-y-1">
              {(project.understanding?.top_modules || []).slice(0, 6).map((m) => (
                <div key={m} className="font-mono truncate">{m}/</div>
              ))}
              {(project.understanding?.manifests || []).slice(0, 6).map((m) => (
                <div key={m} className="text-teal font-mono truncate">📦 {m}</div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-cream">
            <div className="px-3 py-2.5 border-b border-border text-xs uppercase tracking-widest font-bold text-ink2 flex items-center gap-2">
              <FileCode2 className="w-3.5 h-3.5" /> Files
            </div>
            <div className="max-h-[45vh] overflow-y-auto py-1" data-testid="kailash-file-tree">
              {flatFiles.map((row) => {
                if (row.kind === "dir") {
                  return (
                    <button
                      key={"d-" + row.path}
                      onClick={() => toggleDir(row.path)}
                      className="w-full text-left px-2 py-1 hover:bg-paper flex items-center gap-1.5 text-sm"
                      style={{ paddingLeft: `${row.depth * 12 + 8}px` }}
                    >
                      {row.isOpen ? <ChevronDown className="w-3 h-3 text-ink2" /> : <ChevronRight className="w-3 h-3 text-ink2" />}
                      {row.isOpen ? <FolderOpen className="w-3.5 h-3.5 text-ink2" /> : <Folder className="w-3.5 h-3.5 text-ink2" />}
                      <span className="truncate">{row.name}</span>
                    </button>
                  );
                }
                return (
                  <button
                    key={"f-" + row.id}
                    onClick={() => openFile(row.id)}
                    className={`w-full text-left px-2 py-1 hover:bg-paper flex items-center gap-1.5 text-sm ${selected?.id === row.id ? "bg-paper" : ""}`}
                    style={{ paddingLeft: `${row.depth * 12 + 24}px` }}
                    data-testid={`kailash-file-${row.id}`}
                  >
                    <FileCode2 className="w-3.5 h-3.5 text-ink2" />
                    <span className="truncate">{row.name}</span>
                  </button>
                );
              })}
              {!flatFiles.length && <div className="p-3 text-xs text-ink2 text-center">No files indexed yet.</div>}
            </div>
          </div>

          {/* Generated configs */}
          <div className="rounded-2xl border border-border bg-cream">
            <div className="px-3 py-2.5 border-b border-border text-xs uppercase tracking-widest font-bold text-ink2 flex items-center gap-2">
              <Package className="w-3.5 h-3.5" /> Generated configs ({configs.length})
            </div>
            <div className="p-2 space-y-1 max-h-[40vh] overflow-y-auto" data-testid="kailash-config-list">
              {configs.map((cfg) => (
                <div key={cfg.id} className={`group px-2 py-2 rounded-lg hover:bg-paper cursor-pointer ${activeConfig?.id === cfg.id ? "bg-paper" : ""}`} onClick={() => openConfig(cfg)}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{cfg.distro_name} · {cfg.arch}</div>
                      <div className="text-[10px] text-ink2 uppercase tracking-widest font-mono">{cfg.package_format} · {cfg.status}</div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); deleteConfig(cfg.id); }} className="opacity-0 group-hover:opacity-100 text-ink2 hover:text-marigold"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {!configs.length && <div className="p-3 text-xs text-ink2 text-center">No build configs yet.</div>}
            </div>
          </div>

          {/* Compile → Package stage indicator */}
          <CompileStagePanel projectId={id} project={project} onProjectRefresh={() => api.get(`/kailash/projects/${id}`).then(({ data }) => setProject(data))} />
        </aside>

        {/* Center — viewer */}
        <main className="col-span-6">
          <div className="rounded-2xl border border-border bg-cream min-h-[80vh]" data-testid="kailash-viewer">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-3">
              <div className="text-xs uppercase tracking-widest font-bold text-ink2">
                {mode === "file"
                  ? (selected?.path || "Select a file")
                  : (activeConfig ? `${activeConfig.distro_name} · ${activeConfig.arch} · ${activeConfig.package_format}` : "Select a config")}
              </div>
              {mode === "config" && activeConfig && (
                <button onClick={() => downloadConfig(activeConfig)} className="btn-ghost text-xs" data-testid="kailash-config-download">
                  <Download className="w-3.5 h-3.5" /> Download JSON
                </button>
              )}
            </div>

            {contentBusy && <div className="p-8 text-center text-ink2"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>}

            {mode === "file" && !contentBusy && (
              selected ? (
                <pre className="p-4 text-xs font-mono overflow-x-auto whitespace-pre-wrap leading-relaxed max-h-[75vh] overflow-y-auto"><code>{content}</code></pre>
              ) : (
                <div className="p-16 text-center text-ink2" data-testid="kailash-viewer-empty">
                  <Hammer className="w-8 h-8 mx-auto mb-3" />
                  <p className="text-sm">Pick a file to inspect the source, or generate a build config on the right.</p>
                </div>
              )
            )}

            {mode === "config" && activeConfig && (
              <ConfigView cfg={activeConfig} />
            )}
          </div>
        </main>

        {/* Right — target picker */}
        <aside className="col-span-3 space-y-4">
          <div className="rounded-2xl border-2 border-ink bg-ink text-cream" data-testid="kailash-target-picker">
            <div className="px-4 py-3 border-b border-cream/10 text-xs uppercase tracking-widest font-bold text-cream/70 flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5" /> Linux target
            </div>
            <div className="p-4 space-y-3 text-sm">
              <Row label="Distro">
                <select value={pick.distro} onChange={(e) => setPick({ ...pick, distro: e.target.value, version: targets.distros.find((d) => d.id === e.target.value)?.versions[0] || "" })} className="w-full bg-cream/10 border border-cream/20 rounded px-2 py-1.5 text-cream" data-testid="kailash-select-distro">
                  {targets.distros.map((d) => (<option key={d.id} value={d.id} className="text-ink">{d.name}</option>))}
                </select>
              </Row>
              <Row label="Version">
                <select value={pick.version} onChange={(e) => setPick({ ...pick, version: e.target.value })} className="w-full bg-cream/10 border border-cream/20 rounded px-2 py-1.5 text-cream" data-testid="kailash-select-version">
                  {(distroMeta?.versions || []).map((v) => (<option key={v} value={v} className="text-ink">{v}</option>))}
                </select>
              </Row>
              <Row label="Architecture">
                <div className="flex gap-2">
                  {targets.arches.map((a) => (
                    <button key={a.id} onClick={() => setPick({ ...pick, arch: a.id })} className={`flex-1 text-xs py-1.5 rounded border ${pick.arch === a.id ? "bg-marigold border-marigold text-cream" : "bg-cream/5 border-cream/20 text-cream/80 hover:bg-cream/10"}`} data-testid={`kailash-arch-${a.id}`}>
                      {a.id === "x86_64" ? "x86_64" : "ARM64"}
                    </button>
                  ))}
                </div>
              </Row>
              <Row label="Package format">
                <select value={pick.package_format} onChange={(e) => setPick({ ...pick, package_format: e.target.value })} className="w-full bg-cream/10 border border-cream/20 rounded px-2 py-1.5 text-cream" data-testid="kailash-select-format">
                  {allowedFormats.map((f) => (<option key={f.id} value={f.id} className="text-ink">{f.name}</option>))}
                </select>
              </Row>
              <Row label="Notes (optional)">
                <textarea value={pick.notes} onChange={(e) => setPick({ ...pick, notes: e.target.value })} placeholder="e.g. must run as systemd service" rows={2} className="w-full bg-cream/10 border border-cream/20 rounded px-2 py-1.5 text-cream text-xs resize-none" data-testid="kailash-notes" />
              </Row>
              <button onClick={generateConfig} disabled={busy} className="w-full btn-primary" data-testid="kailash-generate-config">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate configuration</>}
              </button>
              <button onClick={() => { setShowCross(true); if (crossTargets.length === 0) setCrossTargets([{ ...pick }]); }} className="w-full text-xs text-cream/80 hover:text-marigold py-1.5 border-t border-cream/10 mt-1" data-testid="kailash-cross-open">
                + Cross-compile many targets at once
              </button>
              <div className="text-[10px] text-cream/50 leading-relaxed pt-2 border-t border-cream/10 inline-flex items-start gap-1.5">
                <ShieldAlert className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>Kailash produces configuration files a real, isolated builder needs. Nothing is compiled here — you take the output to your own runner.</span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-cream p-4 text-xs text-ink2">
            <div className="uppercase tracking-widest font-bold text-ink2 text-[10px] mb-2 inline-flex items-center gap-1.5"><Boxes className="w-3 h-3" /> Cross-links</div>
            <Link to={`/knuth`} className="block py-1 hover:text-marigold">
              <NotebookPen className="w-3 h-3 inline mr-1" /> Document this project with Knuth
            </Link>
          </div>
        </aside>
      </div>

      {/* Cross-compile modal */}
      {showCross && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4" onClick={() => !crossBusy && setShowCross(false)}>
          <div className="bg-cream border-2 border-ink rounded-3xl shadow-[10px_10px_0_0_#1A1B41] w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="kailash-cross-modal">
            <div className="p-4 border-b-2 border-ink flex items-center justify-between bg-saffron/20">
              <div>
                <div className="label-eyebrow mb-1">Cross-compile</div>
                <div className="font-heading text-xl font-semibold">One project → many Linux releases</div>
              </div>
              <button onClick={() => !crossBusy && setShowCross(false)} className="text-ink2 hover:text-ink text-xl font-bold w-8 h-8">×</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-3" data-testid="kailash-cross-targets">
              {crossTargets.map((t, idx) => (
                <div key={idx} className="p-3 rounded-xl border border-border bg-cream space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest font-bold text-ink2">Target {idx + 1}</div>
                    {crossTargets.length > 1 && (
                      <button onClick={() => setCrossTargets(crossTargets.filter((_, i) => i !== idx))} className="text-ink2 hover:text-marigold text-xs">Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <TargetSelect label="Distro" value={t.distro} onChange={(v) => {
                      const nT = { ...t, distro: v, version: targets.distros.find((d) => d.id === v)?.versions[0] || "" };
                      const fam = targets.distros.find((d) => d.id === v)?.family || "any";
                      const fmts = targets.package_formats[fam] || [];
                      if (!fmts.find((f) => f.id === t.package_format)) nT.package_format = fmts[0]?.id || "tar.gz";
                      setCrossTargets(crossTargets.map((x, i) => i === idx ? nT : x));
                    }} options={targets.distros.map((d) => ({ value: d.id, label: d.name }))} />
                    <TargetSelect label="Version" value={t.version} onChange={(v) => setCrossTargets(crossTargets.map((x, i) => i === idx ? { ...t, version: v } : x))}
                      options={(targets.distros.find((d) => d.id === t.distro)?.versions || []).map((v) => ({ value: v, label: v }))} />
                    <TargetSelect label="Arch" value={t.arch} onChange={(v) => setCrossTargets(crossTargets.map((x, i) => i === idx ? { ...t, arch: v } : x))}
                      options={targets.arches.map((a) => ({ value: a.id, label: a.name }))} />
                    <TargetSelect label="Format" value={t.package_format} onChange={(v) => setCrossTargets(crossTargets.map((x, i) => i === idx ? { ...t, package_format: v } : x))}
                      options={(targets.package_formats[targets.distros.find((d) => d.id === t.distro)?.family || "any"] || []).map((f) => ({ value: f.id, label: f.name }))} />
                  </div>
                </div>
              ))}
              {crossTargets.length < 8 && (
                <button onClick={() => setCrossTargets([...crossTargets, { ...pick }])} className="w-full text-xs py-2 border-2 border-dashed border-border rounded-xl hover:border-ink/40 text-ink2" data-testid="kailash-cross-add-target">
                  + Add another target ({crossTargets.length}/8)
                </button>
              )}
              {crossResult && (
                <div className="p-3 rounded-xl bg-teal/10 border-2 border-teal/30" data-testid="kailash-cross-result">
                  <div className="text-sm font-medium text-ink mb-2 flex items-center gap-2">
                    {crossResult.status === "packaged" ? "✓" : crossResult.status === "failed" ? "✗" : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {crossResult.status === "packaged"
                      ? `Bundled ${crossResult.results.length} artifact${crossResult.results.length === 1 ? "" : "s"} · ${(crossResult.artifact_bytes / 1024).toFixed(1)} KB`
                      : crossResult.status === "failed"
                        ? `Build failed — all ${crossResult.failed.length} target(s) errored`
                        : `${crossResult.status || "queued"} · ${crossResult.results.length}/${crossTargets.length} done`}
                  </div>
                  {crossResult.failed?.length > 0 && (
                    <div className="text-xs text-marigold mb-2">{crossResult.failed.length} target(s) failed — see manifest.json inside the bundle.</div>
                  )}
                  <div className="space-y-1 text-xs font-mono text-ink2 mb-3">
                    {(crossResult.results || []).map((r) => <div key={r.run_id}>· {r.artifact_name}</div>)}
                  </div>
                  {crossResult.status === "packaged" && (
                    <button onClick={() => {
                      const token = localStorage.getItem("dpa_token");
                      fetch(`${API}/kailash/cross-builds/${crossResult.id}/artifact`, { headers: { Authorization: `Bearer ${token}` } })
                        .then((r) => r.blob()).then((b) => {
                          const url = URL.createObjectURL(b);
                          const a = document.createElement("a"); a.href = url; a.download = crossResult.artifact_name; a.click();
                          URL.revokeObjectURL(url);
                        });
                    }} className="btn-primary text-xs w-full" data-testid="kailash-cross-download">
                      <Download className="w-3.5 h-3.5" /> Download bundle
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t-2 border-ink flex gap-2 bg-cream">
              <button onClick={() => setShowCross(false)} disabled={crossBusy} className="btn-outline-ink text-sm flex-1">Close</button>
              <button
                onClick={async () => {
                  setCrossBusy(true);
                  setCrossResult(null);
                  try {
                    // Kick off the async batch and poll for completion
                    const { data: kick } = await api.post(`/kailash/projects/${id}/cross-compile`, { targets: crossTargets });
                    const batchId = kick.id;
                    // Poll every 3s for up to 3 minutes
                    let latest = kick;
                    for (let i = 0; i < 60; i++) {
                      await new Promise((r) => setTimeout(r, 3000));
                      const { data: poll } = await api.get(`/kailash/cross-builds/${batchId}`);
                      latest = poll;
                      setCrossResult({ ...poll, results: poll.results || [], failed: poll.failed || [] });
                      if (poll.status === "packaged" || poll.status === "failed") break;
                    }
                    if (latest.status === "packaged") {
                      const { data: cfgs } = await api.get(`/kailash/projects/${id}/build-configs`);
                      setConfigs(cfgs.configs || []);
                      toast.success(`Bundled ${latest.results.length} target${latest.results.length === 1 ? "" : "s"}`);
                    } else if (latest.status === "failed") {
                      toast.error(`All targets failed`);
                    } else {
                      toast.info("Still building — check back in a moment");
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Cross-compile failed");
                  } finally { setCrossBusy(false); }
                }}
                disabled={crossBusy || crossTargets.length === 0} className="btn-primary text-sm flex-1" data-testid="kailash-cross-run">
                {crossBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Building {crossTargets.length}…</> : <><Sparkles className="w-4 h-4" /> Build all {crossTargets.length} targets</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TargetSelect({ label, value, onChange, options }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-paper border border-border rounded px-2 py-1.5 text-sm">
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-cream/60 mb-1">{label}</div>
      {children}
    </div>
  );
}

function ConfigView({ cfg }) {
  const [runs, setRuns] = useState([]);
  const [building, setBuilding] = useState(false);
  const [ghSyncing, setGhSyncing] = useState(false);
  const [ghSync, setGhSync] = useState(cfg.github_sync || null);
  const g = cfg.generated || {};
  const files = g.files || [];
  const canBuild = ["deb", "tar.gz", "docker", "rpm", "pkgbuild"].includes(cfg.package_format);
  const ciEligible = ["deb", "rpm", "tar.gz", "docker"].includes(cfg.package_format);

  useEffect(() => {
    api.get(`/kailash/build-configs/${cfg.id}/runs`).then(({ data }) => setRuns(data.runs || [])).catch(() => {});
    setGhSync(cfg.github_sync || null);
  }, [cfg.id, cfg.github_sync]);

  const pushToGithub = async () => {
    const suggested = prompt("Push a Kailash workflow to which repo? (owner/name)", "");
    if (!suggested || !suggested.includes("/")) return;
    setGhSyncing(true);
    try {
      const { data } = await api.post(`/kailash/build-configs/${cfg.id}/gh-action`, { repo_full_name: suggested.trim() });
      setGhSync(data);
      toast.success("GitHub Action pushed — check the Actions tab.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not push to GitHub");
    } finally { setGhSyncing(false); }
  };

  const build = async () => {
    setBuilding(true);
    try {
      const { data } = await api.post(`/kailash/build-configs/${cfg.id}/build`);
      setRuns((r) => [data, ...r]);
      toast.success(`Built ${data.artifact_name} (${(data.artifact_bytes/1024).toFixed(1)} KB)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
    } finally { setBuilding(false); }
  };

  const download = (run) => {
    const token = localStorage.getItem("dpa_token");
    fetch(`${API}/kailash/build-runs/${run.id}/artifact`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = run.artifact_name || "artifact"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="p-5 space-y-6 max-h-[80vh] overflow-y-auto" data-testid="kailash-config-view">
      {/* Status */}
      <StatusStrip current={cfg.status} />

      {/* Real-build panel */}
      <div className="p-4 rounded-2xl border-2 border-ink bg-ink text-cream" data-testid="kailash-build-panel">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-cream/60 mb-1">Real build runner</div>
            <div className="text-sm">
              {canBuild
                ? <>Ship a real <code className="font-mono text-marigold">{cfg.package_format}</code> from this configuration.</>
                : <>Kailash can&apos;t build <code className="font-mono">{cfg.package_format}</code> in-platform yet. Use the generated config on your own isolated runner.</>}
            </div>
          </div>
          {canBuild && (
            <button onClick={build} disabled={building} className="btn-primary text-sm" data-testid="kailash-build-run">
              {building ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</> : <>Build now</>}
            </button>
          )}
        </div>
        {ciEligible && (
          <div className="pt-3 border-t border-cream/10 mt-3 flex items-center justify-between gap-2">
            <div className="text-xs text-cream/70">
              <span className="uppercase tracking-widest font-bold text-cream/50">CI · GitHub Actions</span>
              {ghSync ? (
                <div className="mt-1"><a className="text-marigold hover:underline" href={ghSync.workflow_url} target="_blank" rel="noopener noreferrer">Synced to {ghSync.repo}@{ghSync.branch}</a></div>
              ) : (
                <div className="mt-1 text-cream/50">Push a workflow so every commit builds this config.</div>
              )}
            </div>
            <button onClick={pushToGithub} disabled={ghSyncing} className="btn-outline-ink text-xs !border-cream/40 !text-cream hover:!bg-cream hover:!text-ink" data-testid="kailash-gh-sync">
              {ghSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><GitBranch className="w-3.5 h-3.5" /> {ghSync ? "Re-sync" : "Push to GitHub"}</>}
            </button>
          </div>
        )}
        {runs.length > 0 && (
          <div className="pt-3 border-t border-cream/10 space-y-1.5" data-testid="kailash-runs-list">
            {runs.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${r.status === "packaged" ? "bg-teal" : r.status === "build_failed" ? "bg-marigold" : "bg-saffron"}`} />
                  <span className="font-mono truncate">{r.artifact_name || "(no artifact)"}</span>
                  <span className="text-cream/50">{r.artifact_bytes ? `${(r.artifact_bytes/1024).toFixed(1)} KB` : ""}</span>
                </div>
                {r.artifact_name && (
                  <button onClick={() => download(r)} className="text-marigold hover:underline inline-flex items-center gap-1" data-testid={`kailash-run-download-${r.id}`}>
                    <Download className="w-3 h-3" /> Download
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {g.summary && (
        <div className="p-3 rounded-xl bg-saffron/10 border border-saffron/30 text-sm text-ink leading-relaxed">
          <div className="text-[10px] uppercase tracking-widest font-bold text-saffron mb-1">Summary (Generated Configuration)</div>
          {g.summary}
        </div>
      )}

      {(g.detected_stack || []).length > 0 && (
        <Section title="Detected stack">
          <ul className="text-sm text-ink2 space-y-1 list-disc pl-5">
            {g.detected_stack.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </Section>
      )}

      {(g.system_dependencies || []).length > 0 && (
        <Section title="System dependencies">
          <div className="space-y-1">
            {g.system_dependencies.map((d, i) => (
              <div key={i} className="text-sm">
                <code className="font-mono text-marigold">{d.name}</code>
                {d.reason && <span className="text-ink2"> — {d.reason}</span>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {(g.build_steps || []).length > 0 && (
        <Section title="Build steps">
          <pre className="p-3 rounded-lg bg-ink text-cream text-xs font-mono overflow-x-auto whitespace-pre-wrap">
            {g.build_steps.map((s) => `$ ${s}`).join("\n")}
          </pre>
        </Section>
      )}

      {(g.install_steps || []).length > 0 && (
        <Section title="Install steps">
          <pre className="p-3 rounded-lg bg-ink text-cream text-xs font-mono overflow-x-auto whitespace-pre-wrap">
            {g.install_steps.map((s) => `$ ${s}`).join("\n")}
          </pre>
        </Section>
      )}

      {files.length > 0 && (
        <Section title={`Generated files (${files.length})`}>
          <div className="space-y-3">
            {files.map((f, i) => (
              <div key={i} className="rounded-xl border border-border overflow-hidden">
                <div className="px-3 py-2 bg-paper flex items-center justify-between gap-2">
                  <code className="font-mono text-xs text-ink font-medium">{f.path}</code>
                  <span className="text-[10px] uppercase tracking-widest text-ink2">{f.language || ""}</span>
                </div>
                {f.purpose && <div className="px-3 py-1.5 text-xs text-ink2 border-b border-border">{f.purpose}</div>}
                <pre className="p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap bg-cream max-h-72 overflow-y-auto">{f.content || ""}</pre>
              </div>
            ))}
          </div>
        </Section>
      )}

      {g.packaging_command && (
        <Section title="Packaging command">
          <pre className="p-3 rounded-lg bg-ink text-cream text-xs font-mono overflow-x-auto whitespace-pre-wrap">$ {g.packaging_command}</pre>
        </Section>
      )}

      {(g.post_build_checks || []).length > 0 && (
        <Section title="Post-build checks">
          <ul className="text-sm text-ink2 space-y-1 list-disc pl-5">
            {g.post_build_checks.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </Section>
      )}

      {(g.risks || []).length > 0 && (
        <Section title="Risks & unknowns">
          <div className="p-3 rounded-xl bg-marigold/10 border border-marigold/30 text-sm">
            <ul className="space-y-1 list-disc pl-5 text-ink2">
              {g.risks.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mb-2 inline-flex items-center gap-1.5"><Terminal className="w-3 h-3" /> {title}</div>
      {children}
    </div>
  );
}

function StatusStrip({ current }) {
  const currIdx = STATUS_STEPS.findIndex((s) => s.id === current);
  return (
    <div className="flex items-center gap-1" data-testid="kailash-status-strip">
      {STATUS_STEPS.map((s, i) => (
        <div key={s.id} className="flex-1 flex items-center gap-1">
          <div className={`flex-1 text-[10px] uppercase tracking-widest font-bold text-center py-1 rounded ${i <= currIdx ? "bg-teal text-cream" : "bg-paper text-ink2"}`}>
            {s.label}
          </div>
          {i < STATUS_STEPS.length - 1 && <div className={`w-2 h-0.5 ${i < currIdx ? "bg-teal" : "bg-paper"}`} />}
        </div>
      ))}
    </div>
  );
}


// ─────────── Compile → Package stage panel ───────────
function CompileStagePanel({ projectId, project, onProjectRefresh }) {
  const [runs, setRuns] = useState([]);
  const [toolchains, setToolchains] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openLogs, setOpenLogs] = useState(null);
  const [hasConfig, setHasConfig] = useState(false);
  const [scaffolding, setScaffolding] = useState(null);   // "c" | "rust" | "go" | null
  const lastCompile = runs[0] || null;
  const compileStatus = project?.last_compile_status;
  const compileLang = project?.last_compile_language;

  const load = () => {
    api.get(`/kailash/projects/${projectId}/compile-runs`).then(({ data }) => setRuns(data.runs || [])).catch(() => setRuns([]));
    api.get(`/kailash/projects/${projectId}/build-configs`).then(({ data }) => setHasConfig((data.configs || []).length > 0)).catch(() => {});
  };
  useEffect(() => { load(); }, [projectId]);   // eslint-disable-line
  useEffect(() => {
    api.get("/kailash/toolchains").then(({ data }) => setToolchains(data)).catch(() => {});
  }, []);

  const compile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/kailash/projects/${projectId}/compile`);
      setRuns((r) => [data, ...r]);
      onProjectRefresh?.();
      if (data.status === "compiled") toast.success(`Compiled ${data.artifact_name} (${(data.artifact_bytes / 1024).toFixed(1)} KB)`);
      else toast.error("Compile failed — see logs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compile failed");
    } finally { setBusy(false); }
  };

  const scaffoldAndCompile = async (language) => {
    setScaffolding(language);
    try {
      await api.post(`/kailash/projects/${projectId}/scaffold-template`, { language });
      toast.success(`${language.toUpperCase()} starter added — compiling now`);
      onProjectRefresh?.();
      // Give the project refresh a beat before invoking compile
      setTimeout(() => compile(), 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not scaffold template");
    } finally {
      setScaffolding(null);
    }
  };

  const download = (run) => {
    if (!run.artifact_name) return;
    const token = localStorage.getItem("dpa_token");
    fetch(`${API}/kailash/compile-runs/${run.id}/artifact`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = run.artifact_name; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const stageIdx = compileStatus === "compiled" ? (hasConfig ? 1 : 0) : 0;
  const packageDisabled = !hasConfig;

  return (
    <div className="rounded-2xl border-2 border-ink bg-ink text-cream" data-testid="kailash-compile-panel">
      <div className="px-3 py-2.5 border-b border-cream/20 text-xs uppercase tracking-widest font-bold text-cream/70 flex items-center gap-2">
        <Cpu className="w-3.5 h-3.5" /> Compile → Package
      </div>

      {/* Stage indicator */}
      <div className="px-3 pt-3">
        <div className="flex items-center gap-1" data-testid="kailash-stage-strip">
          <StageChip idx={0} currIdx={stageIdx} label="Compile" icon={compileStatus === "compiled" ? <CheckCircle2 className="w-3 h-3" /> : compileStatus === "compile_failed" ? <AlertTriangle className="w-3 h-3" /> : <PlayCircle className="w-3 h-3" />} />
          <div className={`w-3 h-0.5 ${stageIdx >= 1 ? "bg-teal" : "bg-cream/20"}`} />
          <StageChip idx={1} currIdx={stageIdx} label="Package" icon={<Package className="w-3 h-3" />} dimmed={packageDisabled} />
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="text-xs text-cream/80 leading-relaxed">
          Compile the sources to a real binary <span className="text-cream/50">before</span> packaging.  Detects the language, shells to the local toolchain, and hands you a downloadable artifact.
        </div>

        {/* Starter templates — one-click Hello for C / Rust / Go */}
        <div data-testid="kailash-template-picker">
          <div className="text-[10px] uppercase tracking-widest font-bold text-cream/50 mb-2">✨ Starter templates</div>
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { lang: "c", label: "Hello C", icon: "🇨" },
              { lang: "rust", label: "Hello Rust", icon: "🦀" },
              { lang: "go", label: "Hello Go", icon: "🐹" },
            ].map((t) => (
              <button
                key={t.lang}
                onClick={() => scaffoldAndCompile(t.lang)}
                disabled={!!scaffolding || busy}
                className={`px-2 py-1.5 rounded-md border border-cream/20 bg-cream/5 hover:bg-marigold/20 hover:border-marigold text-[11px] transition inline-flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid={`kailash-template-${t.lang}`}
                title={scaffolding === t.lang ? "Scaffolding…" : `Drop hello.${t.lang === "rust" ? "rs" : t.lang} into the project`}
              >
                {scaffolding === t.lang ? <Loader2 className="w-3 h-3 animate-spin" /> : <span aria-hidden="true">{t.icon}</span>}
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {toolchains && (
          <div className="text-[10px] font-mono text-cream/60 flex flex-wrap gap-1.5" data-testid="kailash-toolchains">
            {Object.entries(toolchains).map(([lang, t]) => (
              <span key={lang} className={`px-1.5 py-0.5 rounded border ${t.installed ? "border-teal/60 text-teal" : "border-cream/20 text-cream/40 line-through"}`} title={t.installed ? `${t.primary} is available` : t.install_hint}>
                {lang}
              </span>
            ))}
          </div>
        )}

        <button
          onClick={compile}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-marigold text-ink font-bold rounded-full px-4 py-2 text-xs hover:-translate-y-0.5 transition disabled:opacity-50 disabled:transform-none"
          data-testid="kailash-compile-run"
        >
          {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Compiling…</> : <><PlayCircle className="w-3.5 h-3.5" /> Compile now</>}
        </button>

        {lastCompile && (
          <div className="border-t border-cream/10 pt-3 space-y-2 text-xs" data-testid="kailash-compile-runs">
            <div className="text-[10px] uppercase tracking-widest font-bold text-cream/50">Recent runs</div>
            {runs.slice(0, 5).map((r) => (
              <div key={r.id} className="rounded-lg border border-cream/10 p-2" data-testid={`kailash-compile-run-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${r.status === "compiled" ? "bg-teal" : "bg-marigold"}`} />
                    <span className="font-mono text-cream/80 truncate">{r.language} · {r.artifact_name || "(no artifact)"}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.artifact_bytes ? <span className="text-cream/50 text-[10px]">{(r.artifact_bytes / 1024).toFixed(1)} KB</span> : null}
                    {r.artifact_name && (
                      <button onClick={() => download(r)} className="text-marigold hover:underline inline-flex items-center gap-1" data-testid={`kailash-compile-download-${r.id}`}>
                        <Download className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      onClick={() => setOpenLogs(openLogs === r.id ? null : r.id)}
                      className="text-cream/60 hover:text-cream text-[10px] uppercase tracking-widest font-bold"
                      data-testid={`kailash-compile-logs-${r.id}`}
                    >
                      {openLogs === r.id ? "Hide" : "Logs"}
                    </button>
                  </div>
                </div>
                {openLogs === r.id && (
                  <pre className="mt-2 text-[10px] font-mono text-cream/70 bg-black/30 rounded p-2 overflow-x-auto max-h-40 whitespace-pre-wrap" data-testid={`kailash-compile-log-body-${r.id}`}>
                    {(r.logs || []).join("\n")}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}

        {!!compileLang && compileStatus === "compiled" && (
          <div className="text-[10px] text-teal font-mono">✓ Latest compile ({compileLang}) — ready to package.</div>
        )}
      </div>
    </div>
  );
}

function StageChip({ idx, currIdx, label, icon, dimmed = false }) {
  const done = idx < currIdx;
  const current = idx === currIdx && !dimmed;
  return (
    <div
      className={`flex-1 text-[10px] uppercase tracking-widest font-bold text-center py-1 rounded inline-flex items-center justify-center gap-1 ${
        dimmed
          ? "bg-cream/5 text-cream/30 italic"
          : done
            ? "bg-teal text-cream"
            : current
              ? "bg-marigold text-ink"
              : "bg-cream/10 text-cream/50"
      }`}
      title={dimmed ? "Generate a build config first" : ""}
    >
      {icon} {label}
    </div>
  );
}
